import { useEffect } from 'react';
import { useAuth } from 'react-oidc-context';

/**
 * Propagates logout across tabs.
 *
 * When tab A calls auth.signoutRedirect(), the OIDC library removes the
 * `oidc.user:<authority>:<client_id>` key from localStorage. The browser
 * fires a `storage` event in EVERY OTHER tab on the same origin (not the
 * one that made the change). We listen for that specific deletion and
 * push this tab to /login as well.
 *
 * Without this, tab B's React AuthProvider still holds the old user object
 * in memory and the apiClient keeps sending the (cryptographically valid)
 * stale token until it expires.
 */
const CrossTabSync = () => {
  const auth = useAuth();

  useEffect(() => {
    // A GENUINE logout leaves the OIDC user key deleted and gone. A TRANSIENT
    // deletion (a re-issued/failed silent renew, a rapid re-login) has the key
    // rewritten within a moment. So don't bounce on the raw delete event —
    // wait a grace window and bounce only if the key is STILL gone. This stops
    // one tab's transient churn from cascading a /login bounce across every
    // open tab (the multi-tab sign-out storm).
    const GRACE_MS = 3000;
    let graceTimer = null;
    const cancelPending = () => {
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
    };

    const handleStorage = (e) => {
      if (!e.key || !e.key.startsWith('oidc.user:')) return;

      // Key (re)written in another tab = a login/renew, not a logout. Cancel any
      // pending bounce (covers a transient remove-then-set).
      if (e.newValue !== null) {
        cancelPending();
        return;
      }

      // Key deleted elsewhere — verify it's a settled logout before bouncing.
      cancelPending();
      const deletedKey = e.key;
      graceTimer = setTimeout(async () => {
        graceTimer = null;
        // Came back within the grace window → it was transient, not a logout.
        if (window.localStorage.getItem(deletedKey) !== null) return;
        try {
          await auth.removeUser();
        } catch {
          // ignore — we're navigating away anyway
        }
        window.location.replace('/login');
      }, GRACE_MS);
    };

    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('storage', handleStorage);
      cancelPending();
    };
  }, [auth]);

  return null;
};

export default CrossTabSync;
